import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'

import { useUpdateServerName } from '../application/hooks/useUpdateServerName'
import { serversApi } from '../infrastructure/api'

import { Button } from '@/components/Button'
import DefaultModal from '@/components/Modals/DefaultModal'
import { TrimmedInputField } from '@/components/TrimmedInputField'
import { Headline2 } from '@/components/Typography'
import { useModal } from '@/hooks/useModal'
import { DefaultModalProp } from '@/typings/Modals'
import { Flex } from '@/components/Flex'

const MAX_SERVER_NAME_LENGTH = 25

const createValidateNameForm = (existingNames: string[]) =>
  z.object({
    serverName: z
      .string()
      .min(1, 'Server name must be at least 1 character')
      .max(
        MAX_SERVER_NAME_LENGTH,
        `Server name must be no more than ${MAX_SERVER_NAME_LENGTH} characters`,
      )
      .regex(
        /^[a-z0-9]+(-[a-z0-9]+)*$/,
        'Server name can only contain lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen',
      )
      .refine(
        (name) => !existingNames.includes(name),
        'Server name already exists. Please choose a different name.',
      ),
  })

type FormValues = {
  serverName: string
}

export default function EditServerNameModal({
  setOpen,
  ...props
}: DefaultModalProp) {
  const modal = useModal('editServerName')
  const updateServerNameMutation = useUpdateServerName()

  const { data: serversData } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getServers({}),
  })

  const existingServerNames = serversData
    ? serversData
        .filter((server) => server.id !== modal.data.serverId)
        .map((server) => server.name)
    : []

  const validateNameForm = createValidateNameForm(existingServerNames)

  const methods = useForm<FormValues>({
    resolver: zodResolver(validateNameForm),
    defaultValues: {
      serverName: modal.data.serverName ?? '',
    },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = methods

  const onSubmit = async (values: FormValues) => {
    await updateServerNameMutation.mutateAsync({
      id: modal.data.serverId,
      name: values.serverName,
    })

    setOpen(false)
  }

  return (
    <DefaultModal
      {...props}
      type="editServerName"
      setOpen={setOpen}
      modalHeader={<Headline2>Edit Server Name</Headline2>}
      modalBody={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex py="1rem" gap="1rem" flexDirection="column">
              <Flex gap="1.5rem" flexDirection="column">
                <TrimmedInputField
                  name="serverName"
                  control={control}
                  autoFocus
                  placeholder="Enter server name"
                />
                <Button type="submit" disabled={!isValid || isSubmitting}>
                  {isSubmitting ? 'Loading...' : 'Update'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      }
    />
  )
}
